import React from "react";
import { 
  Puzzle, 
  Settings, 
  RefreshCcw, 
  ExternalLink, 
  CheckCircle2, 
  Zap, 
  Database, 
  Cpu, 
  FileText,
  ArrowRight,
  Info,
  ShieldCheck,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const Integrations = () => {
  const integrations = [
    {
      name: "Salesforce",
      description: "Sincroniza automáticamente PQRSD y casos legales con tu instancia de Salesforce.",
      status: "Available",
      icon: "S",
      color: "bg-blue-600",
      type: "CRM",
      lastSync: "Hoy, 10:24 AM"
    },
    {
      name: "HubSpot",
      description: "Gestión de contactos y tickets legales integrados directamente en HubSpot CRM.",
      status: "Available",
      icon: "H",
      color: "bg-orange-600",
      type: "CRM",
      lastSync: "Hace 2 horas"
    },
    {
      name: "Zoho CRM",
      description: "Automatización de procesos legales y tracking de expedientes en Zoho.",
      status: "Soon",
      icon: "Z",
      color: "bg-red-600",
      type: "CRM"
    },
    {
      name: "Microsoft Dynamics",
      description: "Integración profunda con el ecosistema de Microsoft Enterprise.",
      status: "Soon",
      icon: "D",
      color: "bg-blue-800",
      type: "ERP/CRM"
    },
    {
      name: "Zapier",
      description: "Conecta SarAI con más de 5,000 aplicaciones a través de Zapier.",
      status: "Available",
      icon: "Z",
      color: "bg-orange-400",
      type: "Automation"
    }
  ];

  return (
    <div className="p-8 max-w-7xl mx-auto animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Puzzle className="h-8 w-8 text-blue-600" />
            Integraciones Enterprise
          </h1>
          <p className="text-gray-500 font-medium">
            Conecta SarAI con tu ecosistema corporativo para una automatización total.
          </p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 font-bold shadow-lg shadow-blue-600/20">
          <Zap className="mr-2 h-4 w-4" /> Configurar Webhook Personalizado
        </Button>
      </div>

      {/* Main Flow Visualizer */}
      <div className="mb-12 p-8 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-blue-500/10 blur-[100px] -z-0" />
        
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-8 bg-white/10 w-fit px-3 py-1 rounded-full border border-white/10">
            <Settings className="h-3 w-3 animate-spin text-blue-400" />
            <span className="text-[10px] font-bold uppercase tracking-widest">Pipeline de Datos Jurídicos</span>
          </div>
          
          <h2 className="text-2xl font-bold mb-10 text-center">Flujo de Automatización PQRSD → CRM</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
            {/* Start */}
            <div className="col-span-1 flex flex-col items-center">
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center border border-white/20 mb-3 shadow-inner">
                <FileText className="h-6 w-6 text-blue-300" />
              </div>
              <span className="text-xs font-bold text-slate-300">Entrada PQRSD</span>
            </div>

            <div className="hidden md:block col-span-1 text-center opacity-30">
              <ArrowRight className="h-8 w-8 mx-auto" />
            </div>

            {/* Neural Net / AI */}
            <div className="col-span-1 flex flex-col items-center">
              <div className="w-20 h-20 rounded-3xl bg-blue-600 flex items-center justify-center shadow-xl shadow-blue-500/40 relative">
                <Cpu className="h-10 w-10 text-white animate-pulse" />
                <div className="absolute -inset-1 bg-blue-400/30 rounded-3xl blur animate-pulse" />
              </div>
              <span className="text-sm font-black mt-4 bg-gradient-to-r from-blue-200 to-white bg-clip-text text-transparent italic">SarAI Engine</span>
            </div>

            <div className="hidden md:block col-span-1 text-center opacity-30">
              <ArrowRight className="h-8 w-8 mx-auto" />
            </div>

            {/* End CRM */}
            <div className="col-span-1 flex flex-col items-center">
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center border border-white/20 mb-3">
                <Database className="h-6 w-6 text-emerald-400" />
              </div>
              <span className="text-xs font-bold text-slate-300">Destino CRM</span>
            </div>
          </div>
          
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-blue-400 shrink-0" />
              <div>
                <h4 className="text-sm font-bold mb-1">Cifrado de Punto a Punto</h4>
                <p className="text-[11px] text-slate-400">Tus datos legales se transmiten bajo los mÃ¡s altos estÃ¡ndares de seguridad AES-256.</p>
              </div>
            </div>
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3">
              <RefreshCcw className="h-5 w-5 text-indigo-400 shrink-0" />
              <div>
                <h4 className="text-sm font-bold mb-1">Sincronización Real-time</h4>
                <p className="text-[11px] text-slate-400">Cada respuesta generada por la IA se refleja instantáneamente en tu CRM corporativo.</p>
              </div>
            </div>
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
              <div>
                <h4 className="text-sm font-bold mb-1">Validación Automática</h4>
                <p className="text-[11px] text-slate-400">Verificamos la integridad de los datos antes de cada inserción en la base de datos.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {integrations.map((item) => (
          <Card key={item.name} className="border-gray-200 hover:shadow-xl transition-all duration-300 group overflow-hidden">
            <CardHeader className="relative pb-4">
              <div className="flex items-center justify-between mb-2">
                <div className={cn(
                  "w-12 h-12 rounded-xl flex items-center justify-center text-white text-lg font-black shadow-md",
                  item.color
                )}>
                  {item.icon}
                </div>
                {item.status === "Available" ? (
                  <Badge className="bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100">Activo</Badge>
                ) : (
                  <Badge variant="secondary" className="bg-slate-100 text-slate-500">Muy pronto</Badge>
                )}
              </div>
              <CardTitle className="text-xl font-bold text-slate-800">{item.name}</CardTitle>
              <CardDescription className="text-xs font-medium text-slate-500 uppercase tracking-wider">{item.type}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                {item.description}
              </p>
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                {item.status === "Available" ? (
                  <>
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400">
                      <RefreshCcw className="h-3 w-3 animate-spin-slow" />
                      {item.lastSync}
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 text-[11px] font-bold text-blue-600 hover:text-blue-800">
                      Gestionar <ExternalLink className="ml-1.5 h-3 w-3" />
                    </Button>
                  </>
                ) : (
                  <Button variant="secondary" className="w-full text-xs font-bold py-5 bg-slate-50 text-slate-400 border border-slate-200">
                    Notificar cuando esté listo
                  </Button>
                )}
              </div>
            </CardContent>
            {/* Animated blue underline on hover */}
            <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-600 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
          </Card>
        ))}
        
        {/* Custom Integration Card */}
        <Card className="border-dashed border-2 border-gray-200 bg-gray-50/50 flex flex-col items-center justify-center p-8 text-center hover:bg-white hover:border-blue-300 transition-all cursor-pointer group">
          <div className="w-16 h-16 rounded-full bg-white border border-gray-100 flex items-center justify-center mb-4 shadow-sm group-hover:scale-110 transition-transform">
            <Zap className="h-8 w-8 text-blue-500" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-2">Â¿Necesitas un Webhook?</h3>
          <p className="text-sm text-slate-500 mb-6">Integra SarAI con tu propia API o sistema personalizado en minutos.</p>
          <Button variant="outline" className="rounded-full font-bold text-xs px-6">Explorar DocumentaciÃ³n API</Button>
        </Card>
      </div>

      <div className="mt-12 p-6 rounded-2xl bg-amber-50 border border-amber-200 flex items-start gap-4">
        <div className="p-2 bg-amber-100 rounded-lg shrink-0">
          <AlertCircle className="h-6 w-6 text-amber-600" />
        </div>
        <div>
          <h4 className="text-base font-bold text-amber-900 mb-1">Nota sobre datos compartidos</h4>
          <p className="text-sm text-amber-800">
            Al habilitar una integración, SarAI compartirá automáticamente los fragmentos de las peticiones que selecciones con el servicio de destino. Puedes revocar estos permisos en cualquier momento desde la configuración de seguridad.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Integrations;
